import { ProductDetailDialogProvider } from "@/components/product/product-detail-dialog/ProductDetailDialogContext";
import { useHideFloatingButtons } from "@/components/support/useFloatingButtonsLift";
import {
  NestableDrawer,
  NestableDrawerClose,
  NestableDrawerDescription,
  NestableDrawerTitle
} from "@/components/ui/nestable-drawer";
import { NO_DEAD_CLICK_CLASS, PII_MASK_CLASS } from "@/lib/analytics";
import { MAX_MESSAGE_LENGTH } from "@/lib/order-assistant/contract";
import {
  canSend,
  isResendable,
  type Notice
} from "@/lib/order-assistant/conversation";
import { formatResetTime } from "@/lib/order-assistant/format";
import { cn } from "@repo/design-system/utils";
import { XMark } from "@medusajs/icons";
import { Button, IconButton, Kbd, Textarea } from "@medusajs/ui";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ArrowUp, SquarePen } from "lucide-react";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  type FormEvent,
  type KeyboardEvent
} from "react";

import { LinkButton } from "./LinkButton";
import { MessageRow } from "./MessageView";
import { useOrderAssistant } from "./OrderAssistantProvider";
import { ORDER_ASSISTANT_TITLE } from "./OrderAssistantButton";
import { SupplierDetailView } from "./SupplierDetailView";
import { TradeAccountCard } from "./TradeAccountCard";

const STARTER_PROMPTS = [
  "Do you have Wella Koleston 7/0 in 60ml?",
  "Compare supplier prices for Olaplex No. 3",
  "What did we order last time?",
  "Which supplier has the cheapest foils?"
] as const;

function StarterPrompts({ onPick }: { onPick: (text: string) => void }) {
  return (
    <div className="flex flex-1 flex-col justify-center gap-y-5 py-4">
      <div className="space-y-1">
        <h3 className="text-ui-fg-base text-lg font-semibold">
          How can I help with your order?
        </h3>
        <p className="text-ui-fg-subtle text-sm">
          I can look up products, compare supplier prices and check your past
          orders. I can&apos;t add anything to your cart yet.
        </p>
      </div>
      <div className="space-y-2">
        <p className="text-ui-fg-muted text-xs font-medium uppercase tracking-wide">
          Try asking
        </p>
        <ul className="flex flex-col gap-2">
          {STARTER_PROMPTS.map((prompt) => (
            <li key={prompt}>
              <button
                type="button"
                onClick={() => onPick(prompt)}
                className="border-ui-border-base bg-ui-bg-base text-ui-fg-base hover:bg-ui-bg-base-hover focus-visible:shadow-borders-focus w-full rounded-lg border px-3 py-2.5 text-left text-sm focus-visible:outline-none"
              >
                {prompt}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** The banner above the composer for the refusals that are not part of a turn. */
function NoticeBar({ notice }: { notice: Notice }) {
  const { resendDraft, dismissNotice } = useOrderAssistant();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });

  const signIn = () => {
    const here = `${location.pathname}${location.searchStr ?? ""}${
      location.hash ? `#${location.hash}` : ""
    }`;
    void navigate({ to: "/login", search: { redirect: here } });
  };

  const actions = {
    signIn: { label: "Sign in again", onClick: signIn },
    retry: { label: "Try again", onClick: resendDraft },
    dismiss: { label: "Dismiss", onClick: dismissNotice }
  } as const;
  const notices: Record<
    Notice["kind"],
    { message: string; hint?: string; action: keyof typeof actions }
  > = {
    token_expired: {
      message: "Your sign-in has expired.",
      hint: "Your message is saved. Sign in again to send it.",
      action: "signIn"
    },
    busy_session: {
      message:
        "The assistant can't take a new message yet. Try again in a few minutes.",
      action: "retry"
    },
    busy_service: {
      message: "The assistant is busy, try again in a moment.",
      action: "retry"
    },
    mismatch: {
      message:
        "That conversation belongs to a different account, so it was cleared. Send your message again to start a new one.",
      action: "dismiss"
    },
    outdated: {
      message:
        "The assistant has been updated. Refresh the page to keep it working properly.",
      action: "dismiss"
    },
    too_long: {
      // One line: the composer's own hint says how to fix it.
      message: "Nothing was sent because your message is too long.",
      action: "dismiss"
    }
  };

  const { message, hint, action } = notices[notice.kind];
  const { label, onClick } = actions[action];
  return (
    <>
      <p className="text-ui-fg-base">{message}</p>
      {hint ? <p className="text-ui-fg-subtle">{hint}</p> : null}
      <LinkButton onClick={onClick}>{label}</LinkButton>
    </>
  );
}

/** The message-limit sentence by the window that was hit, `generic` when the
 *  service named none. */
const LIMIT_TEXT: Record<"hour" | "day" | "generic", (time: string) => string> =
  {
    hour: (time) =>
      `You've used all your messages for this hour. You can ask again from ${time}.`,
    day: (time) =>
      `You've reached today's message limit. You can ask again from ${time}.`,
    generic: (time) =>
      `You've reached your message limit. You can ask again from ${time}.`
  };

function LimitNotice({
  resetAt,
  limitWindow
}: {
  resetAt: string;
  limitWindow: "hour" | "day" | null;
}) {
  const time = formatResetTime(resetAt, "en-AU");
  return (
    <p className="text-ui-fg-base">
      {time
        ? LIMIT_TEXT[
            limitWindow !== null && Object.hasOwn(LIMIT_TEXT, limitWindow)
              ? limitWindow
              : "generic"
          ](time)
        : LIMIT_TEXT.generic("later")}
    </p>
  );
}

function Composer() {
  const { state, sendable, send, setDraft } = useOrderAssistant();
  const ref = useRef<HTMLTextAreaElement>(null);
  const limited = state.limit !== null && !sendable && !state.inFlight;

  // Grow with the text, up to a few lines.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  }, [state.draft]);

  const submit = useCallback(
    (event?: FormEvent) => {
      event?.preventDefault();
      if (sendable) send(state.draft);
    },
    [sendable, send, state.draft]
  );

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    // Enter that confirms an IME composition is not a send.
    if (event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  // The textarea's maxLength limits typing only; a refused question merged
  // with a follow-up can exceed it.
  const tooLong = state.draft.trim().length > MAX_MESSAGE_LENGTH;
  const canSubmit = sendable && state.draft.trim() !== "" && !tooLong;

  return (
    <form
      onSubmit={submit}
      className="border-ui-border-base flex flex-wrap items-end gap-x-2 gap-y-2 border-t px-4 py-3"
    >
      {tooLong && (
        <p role="alert" className="text-ui-fg-base basis-full text-sm">
          Your message is too long. Shorten it to send.
        </p>
      )}
      <Textarea
        ref={ref}
        rows={1}
        value={state.draft}
        maxLength={MAX_MESSAGE_LENGTH}
        disabled={limited}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        aria-label="Your message"
        placeholder="Ask about a product, a price or an order"
        className="max-h-32 min-h-10 flex-1 resize-none"
      />
      <button
        type="submit"
        disabled={!canSubmit}
        aria-label="Send"
        title="Send"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-600 text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-purple-600"
      >
        <ArrowUp className="h-5 w-5" aria-hidden="true" />
      </button>
    </form>
  );
}

/**
 * The right-hand chat drawer. The conversation itself lives in
 * {@link OrderAssistantProvider}; this renders it, so closing the drawer loses
 * nothing.
 *
 * The drawer content is a Radix portal, which is why it carries the replay mask
 * and the dead-click exclusion itself (ADR-0006): everything on it is a model's
 * answer about the salon's ordering. A `ProductDetailDialogProvider` is mounted
 * inside it because the dialog it renders is itself a modal: rendered from the
 * page behind the drawer it would sit outside this dialog's focus trap and be
 * dead to the pointer. Mounted here, it works from any page.
 */
export function OrderAssistantDrawer() {
  const assistant = useOrderAssistant();
  const {
    state,
    open,
    setOpen,
    send,
    retry,
    startNew,
    supplierDetail,
    closeSupplierDetail
  } = assistant;

  // The floating buttons are `fixed` and `z-50`, above this drawer's panel.
  useHideFloatingButtons(open);

  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };

  // Follow the answer as it grows, unless the salon scrolled up to read.
  // A new message always scrolls to it.
  const last = state.messages[state.messages.length - 1];
  const messageCount = state.messages.length;
  const seenCount = useRef(messageCount);
  useLayoutEffect(() => {
    if (messageCount !== seenCount.current) {
      seenCount.current = messageCount;
      stickToBottom.current = true;
    }
    const el = scrollRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [state.messages, state.notice, open, messageCount]);

  // The live region announces a turn starting and its finished text once, never
  // each streamed delta (the transcript itself is not a live region).
  const answering = last?.role === "assistant" && last.status === "streaming";
  const finished =
    last?.role === "assistant" && last.status === "done" ? last.text : "";
  const announcement = answering
    ? "Answering your message"
    : finished
      ? `The assistant has answered. ${finished}`
      : "";

  // The same gate `retry` applies (turn running, ended, disabled, limit): an
  // "Ask again" that would do nothing is not offered.
  const canRetry = isResendable(last) && canSend({ ...state, notice: null });

  return (
    <NestableDrawer
      open={open}
      onOpenChange={setOpen}
      className={cn(PII_MASK_CLASS, NO_DEAD_CLICK_CLASS)}
      // Escape steps back out of the supplier detail view before it closes the
      // drawer. (The product dialog on top has its own layer: Escape reaches
      // only the top-most one.)
      onEscapeKeyDown={(event) => {
        if (supplierDetail) {
          event.preventDefault();
          closeSupplierDetail();
        }
      }}
    >
      <ProductDetailDialogProvider>
        <div className="border-ui-border-base flex items-center justify-between gap-x-2 border-b px-4 py-3">
          <div className="min-w-0">
            <NestableDrawerTitle className="text-ui-fg-base truncate text-lg font-semibold leading-normal tracking-normal">
              {ORDER_ASSISTANT_TITLE}
            </NestableDrawerTitle>
            <NestableDrawerDescription className="sr-only">
              Ask about products, supplier prices and your past orders.
            </NestableDrawerDescription>
          </div>
          <div className="flex shrink-0 items-center gap-x-1">
            {state.messages.length > 0 ? (
              <IconButton
                size="small"
                type="button"
                variant="transparent"
                onClick={startNew}
                aria-label="New conversation"
                title="New conversation"
              >
                <SquarePen className="h-4 w-4" aria-hidden="true" />
              </IconButton>
            ) : null}
            <Kbd className="max-sm:hidden">esc</Kbd>
            <NestableDrawerClose asChild>
              <IconButton
                size="small"
                type="button"
                variant="transparent"
                aria-label="Close order assistant"
              >
                <XMark />
              </IconButton>
            </NestableDrawerClose>
          </div>
        </div>

        {supplierDetail ? (
          <SupplierDetailView
            key={supplierDetail.supplierProductId}
            target={supplierDetail}
            onClose={closeSupplierDetail}
          />
        ) : null}

        {/* The conversation stays mounted behind the detail view so its
              scroll position and any running stream survive the round trip. */}
        <div
          hidden={supplierDetail !== null}
          className={cn(
            "min-h-0 flex-1 flex-col",
            // `hidden` alone loses to `flex` (same specificity, later rule).
            supplierDetail ? "hidden" : "flex"
          )}
        >
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="flex min-h-0 flex-1 flex-col gap-y-4 overflow-y-auto px-4 py-4"
          >
            {state.messages.length === 0 ? (
              <>
                <TradeAccountCard onNavigate={() => setOpen(false)} />
                <StarterPrompts onPick={send} />
              </>
            ) : (
              state.messages.map((message, index) => (
                <MessageRow
                  key={message.id}
                  message={message}
                  isLast={index === state.messages.length - 1}
                  canRetry={canRetry}
                  suggestionsEnabled={!state.inFlight && !state.ended}
                  onRetry={retry}
                  onSuggestion={send}
                />
              ))
            )}
          </div>

          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {announcement}
          </div>

          {state.notice || state.limit || state.ended ? (
            <div
              role="status"
              className="border-ui-border-base bg-ui-bg-subtle flex flex-col gap-y-1 border-t px-4 py-3 text-sm"
            >
              {state.limit && !state.ended ? (
                <LimitNotice
                  resetAt={state.limit.resetAt}
                  limitWindow={state.limit.window}
                />
              ) : null}
              {state.notice ? <NoticeBar notice={state.notice} /> : null}
              {state.ended ? (
                <>
                  <p className="text-ui-fg-base">
                    This conversation has ended.{" "}
                    {state.ended.reason === "idle_expired"
                      ? "It was idle for too long."
                      : state.ended.reason === "superseded"
                        ? "You started more conversations, so the oldest one was closed."
                        : ""}
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    onClick={startNew}
                    className="w-fit"
                  >
                    Start a new conversation
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}

          {state.ended ? null : <Composer />}
        </div>
      </ProductDetailDialogProvider>
    </NestableDrawer>
  );
}
